import { useAuthentication } from '@ravago/shared/authentication';
import { getContentfulProductsByIds, getOrderLine, getOrderLineDocuments } from '@ravago/shared/next-data-access/client';
import { NormalizedProduct } from '@ravago/shared/page-data/models/contentful-mapped';

import { useQuery } from '@tanstack/react-query';

import { hasOrderDocuments } from '../../../../utils/order/order-helper.utils';

interface UseOrderLineParams {
   orderId: number;
   orderNumber: number;
   locale: string;
   contentfulProduct?: NormalizedProduct;
}

export const useOrderLine = ({ orderId, orderNumber, locale, contentfulProduct }: UseOrderLineParams) => {
   const { accessToken } = useAuthentication();

   const {
      data: orderLine,
      isLoading: isOrderLineLoading,
      error: orderLineError
   } = useQuery({
      queryKey: ['orderLine', orderId, orderNumber],
      queryFn: async () => {
         const orderLineResponse = await getOrderLine(orderId, orderNumber);
         if (!orderLineResponse) throw new Error('Order line not found');
         // Attach contentfulProduct if available
         if (contentfulProduct) {
            orderLineResponse.contentfulProduct = contentfulProduct;
         } else if (orderLineResponse.product?.pkbId) {
            try {
               const contentfulProductResponse = await getContentfulProductsByIds(locale, [`product-${orderLineResponse.product.pkbId}`]);
               orderLineResponse.contentfulProduct = contentfulProductResponse?.length ? contentfulProductResponse[0] : undefined;
            } catch {
               orderLineResponse.contentfulProduct = undefined;
            }
         }
         return orderLineResponse;
      },
      enabled: !!accessToken
   });

   const {
      data: orderLineDocuments,
      isLoading: isDocumentsLoading,
      error: documentsError
   } = useQuery({
      queryKey: ['orderLineDocuments', orderId, orderNumber],
      queryFn: async () => getOrderLineDocuments(orderId.toString(), orderNumber),
      enabled: !!accessToken
   });

   const hasDocuments = orderLineDocuments ? hasOrderDocuments(orderLineDocuments) : false;

   const isLoading = isOrderLineLoading || isDocumentsLoading;

   return {
      orderLine,
      orderLineDocuments,
      hasDocuments,
      isLoading,
      orderLineError,
      documentsError
   };
};
